import { graphql } from 'gatsby'
import React, { FC, ImgHTMLAttributes, useMemo } from 'react'
import styled from 'styled-components'

import useIsSsr from '../../hooks/useIsSsr'
import useSiteSettings from '../../hooks/useSiteSettings'
import { useDispensaryContext } from '../../lib/dispensaries'
import { dispensaryImageUrlFor } from '../../lib/sanity'
import { useDispensaryLogoCdn } from '../../lib/sanity-dispensaries/query'
import { imageUrlFor } from '../../utils/image-url'

import logoImage from '../../img/logo-designs-for-health.svg'

export interface SiteLogoProps {
  alt?: string
  className?: string
  dfhLogoComponent?: 'img' | FC<ImgHTMLAttributes<HTMLImageElement>>
  dispensaryLogoComponent?: 'img' | FC<ImgHTMLAttributes<HTMLImageElement>>
}

interface DfhSiteLogoProps {
  alt?: string
  className?: string
  component: 'img' | FC<ImgHTMLAttributes<HTMLImageElement>>
}

export const DfhSiteLogo: FC<DfhSiteLogoProps> = ({ alt, className, component: Component }) => {
  const sanitySiteSettings = useSiteSettings()
  const dfhLogoSrc = useMemo(
    () => imageUrlFor(sanitySiteSettings?.mainLogo?._rawAsset).url() || logoImage,
    [sanitySiteSettings?.mainLogo?._rawAsset],
  )

  return <Component className={className} src={dfhLogoSrc} alt={alt || 'Designs for Health'} />
}

interface DispensarySiteLogoProps {
  alt?: string
  className?: string
  dfhLogoComponent: 'img' | FC<ImgHTMLAttributes<HTMLImageElement>>
  dispensaryLogoComponent: 'img' | FC<ImgHTMLAttributes<HTMLImageElement>>
}

const DispensarySiteLogo: FC<DispensarySiteLogoProps> = ({
  alt,
  className,
  dfhLogoComponent: DfhLogoComponent,
  dispensaryLogoComponent: DispensaryLogoComponent,
}) => {
  const logoQuery = useDispensaryLogoCdn()
  const dispensaryLogoSrc = useMemo(
    () =>
      logoQuery._tag === 'Right' && logoQuery.data?.logoImageUrl
        ? dispensaryImageUrlFor(logoQuery.data.logoImageUrl)
            .height(90)
            .dpr(3)
            .auto('format')
            .url() || null
        : null,
    [logoQuery],
  )
  return dispensaryLogoSrc ? (
    <DispensaryLogoComponent
      className={className}
      src={dispensaryLogoSrc}
      alt={alt || 'Dispensary'}
    />
  ) : (
    <DfhSiteLogo alt={alt} className={className} component={DfhLogoComponent} />
  )
}

const SiteLogo: React.FC<SiteLogoProps> = React.memo(
  ({
    alt,
    className,
    dfhLogoComponent: DfhLogoComponent = 'img',
    dispensaryLogoComponent: DispensaryLogoComponent = 'img',
  }) => {
    // Suspense not supported server-side yet
    const isSsr = useIsSsr()
    const dispensary = useDispensaryContext()

    return !isSsr && dispensary ? (
      <React.Suspense fallback={null}>
        <DispensarySiteLogo
          alt={alt}
          className={className}
          dfhLogoComponent={DfhLogoComponent}
          dispensaryLogoComponent={DispensaryLogoComponent}
        />
      </React.Suspense>
    ) : (
      <DfhSiteLogo alt={alt} className={className} component={DfhLogoComponent} />
    )
  },
)
SiteLogo.displayName = 'SiteLogo'

const StyledSiteLogo = styled(SiteLogo)``

export const siteLogoFragment = graphql`
  fragment siteLogo on SanitySiteSettings {
    mainLogo {
      _rawAsset
    }
  }
`

export default StyledSiteLogo
